import React, { useEffect, useRef } from "react";

/**
 * AutoFitText
 * -----------
 * Auto-shrinks its children to fit on a single line within the available
 * container width. The configured font size of the inner content acts
 * as the maximum; the content is scaled down only when it would
 * otherwise overflow.
 *
 * Implementation: writes an inline ``font-size`` to the rendered heading
 * itself. This lets the configured font size act as a maximum while the
 * browser lays out the smaller text normally, so fitting does not move the
 * title by scaling its rendered box around a transform origin.
 *
 * Behaviour notes:
 *   - The container clips horizontally (``overflowX: clip``) so a
 *     pre-scale flash of content doesn't punch through neighboring
 *     layout. ``overflowY`` stays ``visible`` so descenders and
 *     ascenders aren't cropped.
 *   - When the required scale drops below ``MIN_SCALE`` we fall back
 *     to natural wrapping rather than shrinking to an unreadable
 *     pixel size — a 200-character title that scales to 0.05 reads
 *     worse wrapped than tiny.
 * Props:
 *   - children: the content to fit (typically a ``<h1>`` or text node).
 *   - align: ``"left" | "center" | "right"``. Drives the inline-block
 *     placement while the measured text uses ``max-content`` width.
 *   - minScale: optional override for the readability floor below
 *     which we let the content wrap naturally. Defaults to 0.35.
 *
 * The container takes ``width: 100%`` of its parent, so the parent
 * must establish a finite width (most common patterns — block divs
 * inside a sized column, flex items with ``flex: 1`` — already do).
 */
const AutoFitText = React.memo(function AutoFitText({
  children,
  align = "center",
  minScale = 0.35,
}) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const target = content.firstElementChild || content;
    const originalInlineFontSize = target.style.fontSize || "";

    const fit = () => {
      // Reset overrides so we measure at the configured maximum font size.
      target.style.fontSize = originalInlineFontSize;
      content.style.whiteSpace = "nowrap";
      content.style.width = "max-content";

      const availableW = container.clientWidth;
      const naturalW = content.scrollWidth;
      const maxFontSize = parseFloat(window.getComputedStyle(target).fontSize);

      if (
        naturalW > availableW &&
        availableW > 0 &&
        Number.isFinite(maxFontSize)
      ) {
        const scale = availableW / naturalW;
        if (scale >= minScale) {
          target.style.fontSize = `${maxFontSize * scale}px`;
        } else {
          // Below the readability floor — let the content wrap.
          content.style.whiteSpace = "";
          content.style.width = "100%";
        }
      }
    };

    // Defer initial fit to next animation frame so parent layout has
    // settled (otherwise availableW may still be 0 during mount).
    const raf = requestAnimationFrame(fit);

    let ro;
    let mo;
    try {
      ro = new ResizeObserver(() => requestAnimationFrame(fit));
      ro.observe(container);
      ro.observe(content);
    } catch (_) {
      // ResizeObserver unsupported — content stays at natural size.
    }

    // Watch DOM mutations inside content (e.g. user editing text)
    // so the fit recomputes immediately as the user types.
    try {
      mo = new MutationObserver(() => requestAnimationFrame(fit));
      mo.observe(content, { childList: true, subtree: true, characterData: true });
    } catch (_) {
      // MutationObserver unsupported — fit still happens on resize.
    }

    return () => {
      cancelAnimationFrame(raf);
      try { ro?.disconnect(); } catch (_) {}
      try { mo?.disconnect(); } catch (_) {}
    };
  }, [minScale]);

  return (
    <div
      ref={containerRef}
      style={{
        // Clip horizontally so a pre-fit flash never breaks neighbours.
        overflowX: "clip",
        overflowY: "visible",
        // ``minWidth: 0`` lets the container shrink inside flex parents
        // (default ``min-width: auto`` would otherwise pin it to its
        // content's intrinsic width and defeat the whole purpose).
        minWidth: 0,
        width: "100%",
        // ``textAlign`` controls where the measured inline-block sits
        // while its width is ``max-content``.
        textAlign: align,
      }}
    >
      <div
        ref={contentRef}
        style={{
          whiteSpace: "nowrap",
          width: "max-content",
          // ``inline-block`` is required for ALL alignments so the
          // outer ``textAlign`` actually has something to position.
          // (``display: block`` would stretch to the container width
          // and ignore the parent's text alignment entirely.)
          display: "inline-block",
        }}
      >
        {children}
      </div>
    </div>
  );
});

export default AutoFitText;
